// Shared inline SVG icons for the glass UI.
//
// Convention (design rule): SOLID (filled) icons sit on ACCENT surfaces — the
// primary-action FAB, accent buttons. LINEAR (outline / stroke) icons sit on
// SECONDARY surfaces — nav tabs (sidebar + bottom nav), ghost/outline buttons.
// Keep that split when adding icons here.
import React from "react";

type IconProps = { size?: number };

// ─── Accent → SOLID ────────────────────────────────────────────────────────

// Plus / "create" glyph for the mobile primary action (accent FAB circle).
// Filled so it reads as solid on the accent surface.
export const PlusIcon = ({ size = 22 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M13 4a1 1 0 1 0-2 0v7H4a1 1 0 1 0 0 2h7v7a1 1 0 1 0 2 0v-7h7a1 1 0 1 0 0-2h-7V4z" />
  </svg>
);

// ─── Secondary / tabs → LINEAR ──────────────────────────────────────────────

// Linear nav icons render at 100% of their (sized) container span and color
// themselves via `stroke: currentColor` — so the nav rows must NOT force
// `fill-current` on them (that would fill the outlines).
const Line = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);

// Panel / dashboard — four-pane grid.
export const NavPanelIcon = () => (
  <Line>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
  </Line>
);

// Inicio — house.
export const NavHomeIcon = () => (
  <Line>
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10a1 1 0 0 0 1 1h3v-6h6v6h3a1 1 0 0 0 1-1V10" />
  </Line>
);

// Doctores — medical cross in a rounded square.
export const NavDoctorsIcon = () => (
  <Line>
    <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
    <path d="M12 8.5v7M8.5 12h7" />
  </Line>
);

// Ordenes — shopping cart.
export const NavOrdersIcon = () => (
  <Line>
    <circle cx="9" cy="20" r="1.4" />
    <circle cx="18" cy="20" r="1.4" />
    <path d="M2 3h2.2l2.2 11.2a1.6 1.6 0 0 0 1.6 1.3h8.4a1.6 1.6 0 0 0 1.6-1.3L21 7H5.4" />
  </Line>
);

// Pagos — credit card.
export const NavPaymentsIcon = () => (
  <Line>
    <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
    <path d="M2.5 9.5h19" />
    <path d="M6 15h4" />
  </Line>
);

// Trabajadores — users.
export const NavWorkersIcon = () => (
  <Line>
    <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
    <circle cx="9" cy="7.5" r="3.5" />
    <path d="M22 20v-1.5a4 4 0 0 0-3-3.87" />
    <path d="M16 4.13a4 4 0 0 1 0 7.75" />
  </Line>
);

// Caja — banknote.
export const NavCashIcon = () => (
  <Line>
    <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M5.5 12h.01M18.5 12h.01" />
  </Line>
);

// Inventario — stacked boxes / package.
export const NavInventoryIcon = () => (
  <Line>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="m3.27 6.96 8.73 5.04 8.73-5.04" />
    <path d="M12 22.08V12" />
  </Line>
);

// Inventario children — distinct linear glyphs.
// Materiales — stacked layers (discs/blocks).
export const NavMaterialIcon = () => (
  <Line>
    <path d="m12 2 9 5-9 5-9-5 9-5z" />
    <path d="m3 12 9 5 9-5" />
    <path d="m3 17 9 5 9-5" />
  </Line>
);
// Equipos — wrench / tool.
export const NavEquipmentIcon = () => (
  <Line>
    <path d="M14.7 6.3a4 4 0 0 0-5.6 5.6L3 18.4V21h2.6l6.5-6.1a4 4 0 0 0 5.6-5.6l-2.7 2.7-2.3-.6-.6-2.3 2.6-2.8z" />
  </Line>
);
// Proveedores — delivery truck.
export const NavSupplierIcon = () => (
  <Line>
    <path d="M2.5 5.5h11v9h-11z" />
    <path d="M13.5 8.5h4l3 3v3h-7z" />
    <circle cx="6" cy="17.5" r="1.6" />
    <circle cx="17" cy="17.5" r="1.6" />
  </Line>
);
// Órdenes de compra — shopping cart.
export const NavBuyOrderIcon = () => (
  <Line>
    <circle cx="9" cy="20" r="1.4" />
    <circle cx="18" cy="20" r="1.4" />
    <path d="M2 3h2l2.4 12a1 1 0 0 0 1 .8h9.2a1 1 0 0 0 1-.8L20.5 7H6" />
  </Line>
);
// Uso de materiales — clipboard with a check.
export const NavUsageIcon = () => (
  <Line>
    <rect x="5" y="4" width="14" height="17" rx="2" />
    <path d="M9 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1H9z" />
    <path d="m9 13 2 2 4-4" />
  </Line>
);
